import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Zap, Clock, TrendingUp, Database, ChevronDown, ChevronUp, Sparkles, Loader2, Flame } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import { trackUpgradeClick } from '../../utils/analytics';

const Pricing: React.FC = () => {
  const { isPro, isAuthenticated } = useAuth();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const features = [
    {
      category: 'Data & Timing',
      items: [
        { name: 'Data freshness', free: '6 hours delay', pro: 'Real-time', highlight: true },
        { name: 'Historical data', free: '12 hours', pro: '30 days', highlight: true },
        { name: 'Update frequency', free: 'Hourly', pro: 'Every 5 min' },
      ]
    },
    {
      category: 'Analysis Tools',
      items: [
        { name: 'Sentiment Analysis', free: true, pro: true },
        { name: 'Time intervals', free: '60 min only', pro: '5m, 15m, 30m, 1h, 1d' },
        { name: 'WSS (Weighted Sentiment Score)', free: 'Basic', pro: 'Full analysis' },
        { name: 'Sentiment explanation', free: false, pro: true },
        { name: 'Spike detection (5-min)', free: false, pro: true },
        { name: 'Price correlation data', free: false, pro: true },
      ]
    },
  ];

  const faqs = [
    {
      q: 'What does "6 hours delay" mean for FREE users?',
      a: 'FREE users see sentiment data that is 6 hours old. This means if a stock is trending right now, you\'ll see that data 6 hours later. Premium users get real-time data with no delay, allowing them to act on sentiment changes as they happen.'
    },
    {
      q: 'What is WSS (Weighted Sentiment Score)?',
      a: 'WSS is our proprietary algorithm that weighs sentiment based on author credibility, post engagement, and historical accuracy. FREE users see basic WSS metrics, while Premium users get full spike detection, anomaly alerts, and detailed WSS breakdowns.'
    },
    {
      q: 'Can I cancel my Premium subscription anytime?',
      a: 'Yes! You can cancel your subscription at any time. Your Premium access will remain active until the end of your current billing period.'
    },
    {
      q: 'What payment methods do you accept?',
      a: 'We accept all major credit cards (Visa, Mastercard, American Express) and PayPal through our secure payment processor Stripe.'
    },
    {
      q: 'How is sentiment data collected?',
      a: 'We aggregate data from Reddit (including r/wallstreetbets, r/stocks, r/investing, and 50+ other finance subreddits), analyze it using NLP models, and calculate sentiment scores for each ticker mentioned.'
    },
  ];

  const renderValue = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className="w-5 h-5 text-brand-gold" />
      ) : (
        <X className="w-5 h-5 text-white/30" />
      );
    }
    return <span>{value}</span>;
  };

  return (
    <div className="content">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
            Choose Your <span className="text-brand-gold">Trading Edge</span>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Track what the online community is discussing about stocks and crypto. 
            Aggregate social noise and sentiment from Reddit and other platforms.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 pt-6 mb-16">
          {/* FREE Card */}
          <div className="card-glass p-6 sm:p-8 relative">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Free</h2>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-black text-white">$0</span>
                <span className="text-white/50">/month</span>
              </div>
            </div>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 text-white/80">
                <Clock className="w-5 h-5 text-brand-yellow flex-shrink-0" />
                <span><strong className="text-white">6 hours</strong> data delay</span>
              </li>
              <li className="flex items-center gap-3 text-white/80">
                <Database className="w-5 h-5 text-white/50 flex-shrink-0" />
                <span><strong className="text-white">12 hours</strong> of history</span>
              </li>
              <li className="flex items-center gap-3 text-white/80">
                <TrendingUp className="w-5 h-5 text-white/50 flex-shrink-0" />
                <span>All discussed tickers</span>
              </li>
              <li className="flex items-center gap-3 text-white/80">
                <Check className="w-5 h-5 text-white/50 flex-shrink-0" />
                <span>Basic sentiment analysis</span>
              </li>
              <li className="flex items-center gap-3 text-white/80">
                <Check className="w-5 h-5 text-white/50 flex-shrink-0" />
                <span>Basic WSS (Weighted Sentiment Score)</span>
              </li>
            </ul>

            <Link 
              to="/dashboard" 
              className="block w-full py-3 text-center bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors border border-white/20"
            >
              Get Started Free
            </Link>
          </div>

          {/* Premium Card */}
          <div className="bg-brand-card/60 backdrop-blur-md border-2 border-brand-gold/50 rounded-xl p-6 sm:p-8 relative overflow-visible bg-gradient-to-br from-brand-gold/5 to-transparent">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
              <span
                className="px-4 py-1.5 text-sm font-bold rounded-full whitespace-nowrap border"
                style={{
                  color: '#1f1705',
                  borderColor: 'rgba(245, 230, 179, 0.45)',
                  background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 55%, #8B6914 100%)',
                  boxShadow: '0 0 14px rgba(184, 134, 11, 0.35)',
                }}
              >
                MOST POPULAR
              </span>
            </div>
            
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Premium</h2>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-black text-brand-gold">$19.99</span>
                <span className="text-white/50">/mo</span>
              </div>
              <p className="text-sm text-white/70 mt-2">
                Includes the new <strong className="text-brand-gold">Spike tab</strong>.
              </p>
            </div>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5 text-brand-gold flex-shrink-0" />
                <span><strong className="text-brand-gold">Everything in Free</strong></span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Zap className="w-5 h-5 text-brand-gold flex-shrink-0" />
                <span><strong className="text-brand-gold">Real-time data</strong> (no delay)</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Database className="w-5 h-5 text-brand-gold flex-shrink-0" />
                <span><strong className="text-brand-gold">30 days</strong> history (60x)</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Clock className="w-5 h-5 text-brand-gold flex-shrink-0" />
                <span><strong className="text-brand-gold">5 intervals</strong>: 5m, 15m, 30m, 1h, 1d</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <TrendingUp className="w-5 h-5 text-brand-gold flex-shrink-0" />
                <span><strong className="text-brand-gold">Full WSS Analysis</strong></span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5 text-brand-gold flex-shrink-0" />
                <span><strong className="text-brand-gold">Subreddit breakdown</strong></span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Sparkles className="w-5 h-5 text-brand-gold flex-shrink-0" />
                <span><strong className="text-brand-gold">Sentiment explanation</strong></span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5 text-brand-gold flex-shrink-0" />
                <span><strong className="text-brand-gold">Price correlation</strong></span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Flame className="w-5 h-5 text-brand-gold flex-shrink-0" />
                <span><strong className="text-brand-gold">Spike tab</strong></span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <Check className="w-5 h-5 text-brand-gold flex-shrink-0" />
                <span><strong className="text-brand-gold">Extended limits</strong></span>
              </li>
            </ul>

            {isPro ? (
              <div className="space-y-2">
                <div 
                  className="pricing-pro-active-badge w-full py-3 px-4 rounded-lg flex items-center justify-center gap-2 font-semibold"
                >
                  <Check className="w-5 h-5 shrink-0" />
                  You're on Premium
                </div>
                <Link
                  to="/billing"
                  className="block w-full py-2 text-center text-white/80 hover:text-white text-sm font-medium rounded-lg border border-white/20 hover:border-white/40 transition"
                >
                  Manage subscription
                </Link>
              </div>
            ) : isAuthenticated ? (
              <div className="space-y-2">
                {checkoutError && (
                  <p className="text-sm text-red-400">{checkoutError}</p>
                )}
                <button
                  type="button"
                  disabled={checkoutLoading}
                  onClick={async () => {
                    trackUpgradeClick('pricing_card_authenticated', 'start_checkout');
                    setCheckoutError(null);
                    setCheckoutLoading(true);
                    try {
                      const { url } = await apiService.createCheckoutSession();
                      window.location.href = url;
                    } catch (e: any) {
                      setCheckoutError(e?.message || 'Could not start checkout. Try again.');
                    } finally {
                      setCheckoutLoading(false);
                    }
                  }}
                  className="btn btn-gold block w-full py-3 text-center font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {checkoutLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Redirecting…
                    </>
                  ) : (
                    'Upgrade to Premium'
                  )}
                </button>
              </div>
            ) : (
              <Link 
                to={'/login?redirect=' + encodeURIComponent('/billing?checkout=1')} 
                onClick={() => trackUpgradeClick('pricing_card_guest')}
                className="btn btn-gold block w-full py-3 text-center font-bold inline-flex items-center justify-center"
              >
                Upgrade to Premium
              </Link>
            )}
          </div>
        </div>

        {/* Value Highlights */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Why Traders Choose <span className="text-brand-gold">Premium</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="card-glass p-6 text-center">
              <div className="text-4xl font-black text-brand-gold mb-2">60x</div>
              <div className="text-white font-semibold mb-1">More History</div>
              <div className="text-sm text-white/60">30 days vs 12 hours</div>
            </div>
            <div className="card-glass p-6 text-center">
              <div className="text-4xl font-black text-brand-gold mb-2">0</div>
              <div className="text-white font-semibold mb-1">Delay</div>
              <div className="text-sm text-white/60">Real-time vs 6h lag</div>
            </div>
            <div className="card-glass p-6 text-center">
              <div className="text-4xl font-black text-brand-gold mb-2">5x</div>
              <div className="text-white font-semibold mb-1">Intervals</div>
              <div className="text-sm text-white/60">5m, 15m, 30m, 1h, 1d</div>
            </div>
            <div className="card-glass p-6 text-center">
              <div className="text-4xl font-black text-brand-gold mb-2">100%</div>
              <div className="text-white font-semibold mb-1">WSS Access</div>
              <div className="text-sm text-white/60">Full spike detection</div>
            </div>
          </div>
        </div>

        {/* Feature Comparison Table */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Detailed Feature Comparison
          </h2>

          {/* Mobile: stacked cards instead of wide table */}
          <div className="md:hidden space-y-4">
            {features.map((section, sIdx) => (
              <div key={sIdx} className="card-glass p-4 rounded-xl">
                <div className="text-xs font-semibold text-brand-gold uppercase tracking-wide mb-3">
                  {section.category}
                </div>
                <div className="space-y-2">
                  {section.items.map((item, iIdx) => (
                    <div key={iIdx} className="bg-black/20 border border-white/10 rounded-lg p-3">
                      <div className="text-sm text-white font-medium mb-2">{item.name}</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="rounded-md border border-white/10 bg-white/5 p-2">
                          <div className="text-[11px] uppercase tracking-wide text-white/60 mb-1">Free</div>
                          <div className="text-white/85 flex items-center gap-2">{renderValue(item.free)}</div>
                        </div>
                        <div className={`rounded-md border p-2 ${'highlight' in item && item.highlight ? 'border-brand-gold/35 bg-brand-gold/12' : 'border-brand-gold/20 bg-brand-gold/10'}`}>
                          <div className="text-[11px] uppercase tracking-wide text-brand-gold/80 mb-1">Premium</div>
                          <div className={`flex items-center gap-2 ${'highlight' in item && item.highlight ? 'text-brand-gold' : 'text-white'}`}>
                            {renderValue(item.pro)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop/Tablet: table */}
          <div className="hidden md:block pricing-table-wrap table-responsive bg-brand-card/60 backdrop-blur-md border border-brand-border rounded-xl overflow-hidden p-4">
            <table className="w-full table-fixed">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="pricing-th-feature text-left py-5 px-6 text-white/70 font-medium text-base">Feature</th>
                  <th className="pricing-th-plan text-center py-5 px-6 text-white/70 font-medium text-base">Free</th>
                  <th className="pricing-th-plan pricing-th-plan-premium py-5 px-6 text-center text-brand-gold font-bold text-base bg-brand-gold/5">Premium</th>
                </tr>
              </thead>
              <tbody>
                {features.map((section, sIdx) => (
                  <React.Fragment key={sIdx}>
                    <tr className="bg-white/5">
                      <td colSpan={3} className="py-4 px-6 text-sm font-semibold text-brand-gold uppercase tracking-wide">
                        {section.category}
                      </td>
                    </tr>
                    {section.items.map((item, iIdx) => (
                      <tr key={iIdx} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-6 text-white/90 text-base">{item.name}</td>
                        <td className="py-4 px-6 text-center text-white/70 text-base">
                          {renderValue(item.free)}
                        </td>
                        <td className={`py-4 px-6 text-center text-base font-semibold bg-brand-gold/5 ${'highlight' in item && item.highlight ? 'text-brand-gold' : 'text-white'}`}>
                          {renderValue(item.pro)}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-brand-card/60 backdrop-blur-md border border-brand-border rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                >
                  <span className="text-white font-medium pr-4">{faq.q}</span>
                  {openFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-brand-cyan flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-white/50 flex-shrink-0" />
                  )}
                </button>
                {openFaq === idx && (
                  <div className="px-4 pb-4 text-white/70 leading-relaxed border-t border-white/10 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Pricing;
